/**
 * Poynting Antennas — cellular antenna catalog.
 *
 * Poynting specialises in PURE antennas (no router / no modem) that
 * plug into a third-party LTE/5G CPE via SMA. Their lineup spans
 * omni (XPOL-1 family), panel-directional (XPOL-2 family), and
 * multi-purpose vehicle / marine (MIMO-3 series). All units verified
 * against poynting.tech product pages.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const POYNTING_CELLULAR_ANTENNAS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // XPOL-1 — outdoor cross-polarised omni-directional MIMO
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://poynting.tech/antennas/xpol-1-5g/
  // SRC: https://www.broadbandbuyer.com/products/42643-poynting-5g-xpol-1-v2-21/
  //      XPOL-1-5G V2 (2×2 MIMO): cross-polarised omni-directional
  //      outdoor antenna, peak gain 3.5 dBi. Frequency bands:
  //      617-960 MHz, 1427-1517 MHz, 1700-2700 MHz, 3400-3800 MHz
  //      (including 5G CBRS). 5 m SMA male cables included.
  //      Weather + dust resistant enclosure.
  {
    id: 'poynting-xpol-1-5g-v2-2x2',
    manufacturer: 'Poynting',
    model: 'XPOL-1-5G V2 (2×2 MIMO)',
    category: 'cellular-antenna',
    tagline:
      'XPOL-1-5G V2 — outdoor omni 2×2 MIMO, 3.5 dBi, 617-3800 MHz incl. CBRS',
    verified: true,
    datasheetUrl: 'https://poynting.tech/antennas/xpol-1-5g/',
    specs: {
      technology: '5g-sub6',
      bands:
        '617-960 MHz, 1427-1517 MHz, 1700-2700 MHz, 3400-3800 MHz (LTE + 5G NR Sub-6 + CBRS)',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 3.5,
      ipRating: 'IP65', // weather + dust resistant outdoor enclosure
    },
  },

  // SRC: https://poynting.tech/antennas/xpol-1-5g/
  //      XPOL-1-5G V2 (4×4 MIMO): same antenna form factor, 4×4 MIMO
  //      version with peak 3 dBi gain. Useful for Cat 18+ / 5G NR
  //      CPEs that support 4×4 DL MIMO.
  {
    id: 'poynting-xpol-1-5g-v2-4x4',
    manufacturer: 'Poynting',
    model: 'XPOL-1-5G V2 (4×4 MIMO)',
    category: 'cellular-antenna',
    tagline:
      'XPOL-1-5G V2 — outdoor omni 4×4 MIMO, 3 dBi, for Cat 18+ / 5G 4×4 DL CPEs',
    verified: true,
    datasheetUrl: 'https://poynting.tech/antennas/xpol-1-5g/',
    specs: {
      technology: '5g-sub6',
      bands:
        '617-960 MHz, 1427-1517 MHz, 1700-2700 MHz, 3400-3800 MHz (LTE + 5G NR Sub-6 + CBRS)',
      mimo: '4×4',
      pattern: 'omni',
      gainDbi: 3,
      ipRating: 'IP65',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // XPOL-2 — outdoor cross-polarised panel-directional high-gain
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://poynting.tech/antennas/xpol-2-5g/
  // SRC: https://store.orbitalconnect.com/poynting-xpol-2-5g-x-polarised-high-gain-uni-directional-lte-antenna-2x2-mimo-...
  //      XPOL-2-5G: directional high-gain LTE/5G panel, 2×2 MIMO,
  //      11 dBi peak gain. Frequency bands 698-960 MHz + 1710-3800
  //      MHz. Used where signal is weak and a tower direction is
  //      known — panel aimed at the cell tower for max link budget.
  {
    id: 'poynting-xpol-2-5g',
    manufacturer: 'Poynting',
    model: 'XPOL-2-5G',
    category: 'cellular-antenna',
    tagline:
      'XPOL-2-5G — outdoor panel 2×2 MIMO, 11 dBi directional, 698-3800 MHz, weak-signal',
    verified: true,
    datasheetUrl: 'https://poynting.tech/antennas/xpol-2-5g/',
    specs: {
      technology: '5g-sub6',
      bands: '698-960 MHz, 1710-3800 MHz (LTE + 5G NR Sub-6)',
      mimo: '2×2',
      pattern: 'panel',
      beamWidthDeg: 45, // typical for 11 dBi LTE panel antenna
      gainDbi: 11,
      ipRating: 'IP65',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // MIMO-3 — multi-frequency transportation / marine combination
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://poynting.tech/antennas/mimo-3-15/
  // SRC: https://www.amazon.com/Poynting-MIMO-3-15-Transportation-Automotive-410-3800/dp/B09JCFGYXW
  // SRC: https://www.rvmobileinternet.com/gear/poynting-combination-antennas/
  //      MIMO-3-15: 5-in-1 multi-frequency antenna housed in a single
  //      low-profile enclosure — 2× cellular MIMO + 2× dual-band Wi-Fi
  //      MIMO + 1× GPS/GLONASS. Cellular gain up to 6.2 dBi (617 MHz
  //      to 2700 MHz + 450 MHz + 3.5 GHz CBRS). GPS gain up to 21 dB.
  //      Designed for transport / automotive / marine, IP69K rated,
  //      -40..+80 °C operating temperature.
  {
    id: 'poynting-mimo-3-15',
    manufacturer: 'Poynting',
    model: 'MIMO-3-15',
    category: 'cellular-antenna',
    tagline:
      'MIMO-3-15 — 5-in-1 transport/marine combo, 2×2 LTE + Wi-Fi + GPS, 6.2 dBi, IP69K',
    verified: true,
    datasheetUrl: 'https://poynting.tech/antennas/mimo-3-15/',
    specs: {
      technology: '5g-sub6',
      bands:
        '617-2700 MHz + 450 MHz + 3.4-3.8 GHz CBRS (LTE + 5G + GPS)',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 6.2,
      ipRating: 'IP69K',
    },
  },
];
